import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import ZipCodeAutocomplete from '../components/ZipCodeAutocomplete';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeftIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';

// TODO: Define actual options for these once we flesh out the form
const experienceOptions = ['0-1 year', '1-2 years', '3-5 years', '5-10 years', '10-15 years', '15+ years'];
const lessonLocationOptions = ["At student's home", "At my home/studio", "Online"];
const ageGroupOptions = ['Children (5-9)', 'Pre-teens (10-12)', 'Teens (13-17)', 'Adults (18-64)', 'Seniors (65+)'];
const genreOptionsAvailable = [ // Example genres, can be expanded or fetched
  'Pop', 'Rock', 'Classical', 'Jazz', 'Blues', 'Country', 'Folk', 'Electronic', 'R&B/Soul', 'Hip Hop', 'Metal', 'Reggae'
];

// Available languages for selection
const availableLanguages = [
  'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese (Mandarin)', 
  'Chinese (Cantonese)', 'Japanese', 'Korean', 'Russian', 'Arabic', 'Hindi', 'Dutch', 
  'Swedish', 'Norwegian', 'Danish', 'Polish', 'Czech', 'Hungarian', 'Turkish', 'Greek', 
  'Hebrew', 'Thai', 'Vietnamese', 'Indonesian', 'Tagalog', 'Other'
];

// Lesson type options
const lessonTypeOptions = [
  'Online',
  'On-site (teacher\'s studio)',
  'In-home (student\'s home)'
];

// Distance options for travel
const travelDistanceOptions = [
  '5 miles', '10 miles', '15 miles', '20 miles', '25 miles', '30+ miles'
];

// Available skill levels
const skillLevels = ['Beginner', 'Intermediate', 'Advanced'];

// Teaching specialties including genres and teaching focuses
const teachingSpecialties = [
  // Musical Genres
  'Pop', 'Rock', 'Classical', 'Jazz', 'Blues', 'Country', 'Folk', 'Electronic', 
  'R&B/Soul', 'Hip Hop', 'Metal', 'Reggae', 'Latin', 'World Music', 'Gospel',
  // Teaching Focuses
  'Sight-Reading', 'Music Theory', 'Improvisation', 'Composition', 'Song Writing',
  'Competition Preparation', 'Audition Preparation', 'Performance Skills', 
  'Recording Techniques', 'Music Production', 'Ear Training', 'Rhythm Training',
  'Technique Development', 'Music History', 'Ensemble Playing', 'Solo Performance',
  'Music Technology', 'Arrangement', 'Conducting', 'Vocal Coaching'
];

// Available instruments for selection
const availableInstruments = [
  'Piano', 'Guitar', 'Voice/Vocals', 'Violin', 'Drums', 'Bass Guitar', 'Cello', 'Flute', 
  'Saxophone', 'Trumpet', 'Clarinet', 'Ukulele', 'Mandolin', 'Banjo', 'Viola', 'Oboe',
  'French Horn', 'Trombone', 'Tuba', 'Harp', 'Accordion', 'Harmonica', 'Xylophone', 
  'Keyboard/Synthesizer', 'Electric Guitar', 'Acoustic Guitar', 'Double Bass', 'Percussion'
];

// Available days of the week
const daysOfWeek = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

// Available time slots
const timeSlots = [
  '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
  '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM'
];

interface TeacherFormData {
  zipCode: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  password?: string;
  confirmPassword?: string;
  instrumentsTaught: string[];
  specialtyInstrument: string;
  instrumentSkillLevels: { [instrumentName: string]: string[] };
  specialtySkillLevels: { [instrumentName: string]: string };
  profilePictureUrl?: string;
  about: string;
  experience: string;
  locationsOffered: string[];
  agesTaught: string[];
  teachingSpecialties: string[];
  specializedTeachingAreas: string[];
  specializedAgeGroup: string;
  languagesSpoken: string[];
  maxTravelDistance?: string;
  videoIntroUrl?: string;
  educationCertifications?: string;
  websiteSocialUrl?: string;
  // New availability fields
  availability?: {
    [day: string]: boolean; // day -> available (true/false)
  };
  setupAvailabilityLater?: boolean;
}

const TOTAL_STEPS = 8; // Full teacher questionnaire with all steps

const TeacherQuestionnairePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signup } = useAuth();

  const { experience: initialExperience } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<Partial<TeacherFormData>>(() => ({
    experience: initialExperience || '',
    zipCode: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    instrumentsTaught: [],
    specialtyInstrument: '',
    instrumentSkillLevels: {},
    specialtySkillLevels: {},
    about: '',
    locationsOffered: [],
    agesTaught: [],
    teachingSpecialties: [],
    specializedTeachingAreas: [],
    specializedAgeGroup: '',
    languagesSpoken: [],
    maxTravelDistance: '',
    availability: {},
    setupAvailabilityLater: false,
  }));

  // Log form data changes for debugging
  console.log('🔧 TeacherQuestionnairePage - Current step:', currentStep, 'Form data:', formData);

  useEffect(() => {
    console.log('TeacherQuestionnairePage rendered. Step:', currentStep, 'Data:', formData);
    if (!initialExperience) {
      console.warn('Missing experience from previous step, redirecting to /become-a-teacher');
      navigate('/become-a-teacher');
    }
  }, [initialExperience, navigate, currentStep, formData]);

  const handleNext = async () => {
    // Validation for current step's fields
    if (currentStep === 1) {
        if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.phoneNumber?.trim() || !formData.email?.trim() || !formData.password || !formData.confirmPassword || !formData.zipCode?.trim()) {
            alert("Please fill in all fields for account creation.");
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            alert("Passwords do not match.");
            return;
        }
        // Basic email validation
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            alert("Please enter a valid email address.");
            return;
        }
         // Basic phone number validation (e.g., 10 digits)
        if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) { // Removes non-digits for validation
            alert("Please enter a valid 10-digit phone number.");
            return;
        }
        // Basic zip code validation (e.g., 5 digits for US)
        if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
            alert("Please enter a valid zip code.");
            return;
        }
        
        // After step 1 (account creation), call the signup API and then redirect to email verification
        try {
          console.log('Creating teacher account...');
          console.log('Form data being sent:', {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password ? '[HIDDEN]' : 'MISSING',
            role: 'teacher'
          });
          await signup({
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            email: formData.email || '',
            password: formData.password || '',
            role: 'teacher'
          });
          
          console.log('Teacher account created, redirecting to email verification');
          
          // Navigate to email verification page
          navigate('/email-verification', { 
            state: { 
              email: formData.email,
              userType: 'teacher',
              needsQuestionnaire: true // Flag to show they need to complete profile later
            } 
          });
        } catch (error) {
          console.error('Signup failed:', error);
          alert('Signup failed. Please try again.');
        }
        return;
    } else if (currentStep === 2) {
        if (!formData.instrumentsTaught || formData.instrumentsTaught.length === 0) {
            alert("Please select at least one instrument that you teach.");
            return;
        }
        if (!formData.specialtyInstrument?.trim()) {
            alert("Please select your specialty instrument.");
            return;
        }
    } else if (currentStep === 3) {
        // Validate that skill levels are selected for taught instruments
        const taughtInstruments = formData.instrumentsTaught || [];
        const skillLevels = formData.instrumentSkillLevels || {};
        
        for (const instrument of taughtInstruments) {
            if (!skillLevels[instrument] || skillLevels[instrument].length === 0) {
                alert(`Please select skill levels for ${instrument}.`);
                return;
            }
        }
    } else if (currentStep === 4) {
        if (!formData.about?.trim()) {
            alert("Please provide information about yourself and your teaching background.");
            return;
        }
    } else if (currentStep === 5) {
        if (!formData.locationsOffered || formData.locationsOffered.length === 0) {
            alert("Please select at least one lesson location.");
            return;
        }
        if (!formData.agesTaught || formData.agesTaught.length === 0) {
            alert("Please select at least one age group you'd like to teach.");
            return;
        }
    } else if (currentStep === 6) {
        if (!formData.teachingSpecialties || formData.teachingSpecialties.length === 0) {
            alert("Please select at least one teaching specialty.");
            return;
        }
        if (!formData.specializedAgeGroup?.trim()) {
            alert("Please select your specialized age group.");
            return;
        }
    } else if (currentStep === 7) {
        if (!formData.languagesSpoken || formData.languagesSpoken.length === 0) {
            alert("Please select at least one language you speak.");
            return;
        }
    } else if (currentStep === 8) {
        // Availability validation - either set availability or choose to set up later
        const hasAvailability = formData.availability && Object.values(formData.availability).some(available => available);
        if (!hasAvailability && !formData.setupAvailabilityLater) {
            alert("Please either select your available days or choose to set up availability later.");
            return;
        }
    }

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Final submission logic - Submit complete profile
      console.log('Submitting complete teacher profile:', formData);
      
      try {
        // Call the actual signup API
        await signup({
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          email: formData.email || '',
          password: formData.password || '',
          role: 'teacher'
        });
        
        console.log('Teacher account created, redirecting to email verification');
        
        // Navigate to email verification page
        navigate('/email-verification', { 
          state: { 
            email: formData.email,
            userType: 'teacher',
            teacherData: formData // Store for later use after verification
          } 
        });
      } catch (error) {
        console.error('Signup failed:', error);
        alert('Signup failed. Please try again.');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/become-a-teacher'); // Go back to the initial sign-up page if on step 1
    }
  };

  const handleChange = (field: keyof TeacherFormData, value: string | string[] | object | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleMultiCheckboxChange = (field: keyof TeacherFormData, value: string) => {
    setFormData(prev => {
      const currentValues = (prev[field] as string[]) || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value];
      
      // If this is teaching specialties and we're removing a value that's in specialized areas, remove it from there too
      let updatedSpecializedAreas = prev.specializedTeachingAreas || [];
      if (field === 'teachingSpecialties' && currentValues.includes(value) && !newValues.includes(value)) {
        updatedSpecializedAreas = updatedSpecializedAreas.filter(area => area !== value);
      }
      
      return { 
        ...prev, 
        [field]: newValues,
        ...(field === 'teachingSpecialties' ? { specializedTeachingAreas: updatedSpecializedAreas } : {})
      };
    });
  };

  // Helper function to handle skill level changes for instruments
  const handleSkillLevelChange = (instrument: string, skillLevel: string) => {
    setFormData(prev => {
      const currentSkillLevels = prev.instrumentSkillLevels || {};
      const currentSkillsForInstrument = currentSkillLevels[instrument] || [];
      
      const newSkillsForInstrument = currentSkillsForInstrument.includes(skillLevel)
        ? currentSkillsForInstrument.filter(level => level !== skillLevel)
        : [...currentSkillsForInstrument, skillLevel];
      
      // If unchecking a skill level that is currently the specialty, clear the specialty
      const updatedSpecialtySkillLevels = { ...prev.specialtySkillLevels };
      if (currentSkillsForInstrument.includes(skillLevel) && !newSkillsForInstrument.includes(skillLevel)) {
        if (prev.specialtySkillLevels?.[instrument] === skillLevel) {
          delete updatedSpecialtySkillLevels[instrument];
        }
      }
      
      return {
        ...prev,
        instrumentSkillLevels: {
          ...currentSkillLevels,
          [instrument]: newSkillsForInstrument
        },
        specialtySkillLevels: updatedSpecialtySkillLevels
      };
    });
    console.log(`Skill level ${skillLevel} toggled for instrument ${instrument}`);
  };

  // Helper function to handle specialty skill level changes for instruments
  const handleSpecialtySkillLevelChange = (instrument: string, skillLevel: string) => {
    setFormData(prev => {
      const currentSpecialties = prev.specialtySkillLevels || {};
      const currentCount = Object.keys(currentSpecialties).filter(inst => currentSpecialties[inst]).length;
      const instrumentAlreadyHasSpecialty = currentSpecialties[instrument];
      
      // If trying to add a new specialty and already at limit of 2
      if (!instrumentAlreadyHasSpecialty && currentCount >= 2) {
        alert("You can only select specialty skill levels for up to 2 instruments.");
        return prev;
      }
      
      return {
        ...prev,
        specialtySkillLevels: {
          ...currentSpecialties,
          [instrument]: skillLevel
        }
      };
    });
    console.log(`Specialty skill level ${skillLevel} selected for instrument ${instrument}`);
  };

  // Helper function to handle specialized teaching area changes (with limit of 3)
  const handleSpecializedTeachingAreaChange = (value: string) => {
    const currentAreas = formData.specializedTeachingAreas || [];
    const isSelected = currentAreas.includes(value);
    
    if (isSelected) {
      // Remove the area
      const updatedAreas = currentAreas.filter(area => area !== value);
      handleChange('specializedTeachingAreas', updatedAreas);
    } else {
      // Add the area (with max 3 limit)
      if (currentAreas.length < 3) {
        const updatedAreas = [...currentAreas, value];
        handleChange('specializedTeachingAreas', updatedAreas);
      }
    }
  };



  // Main content for each step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Account Creation step based on the new design
        return (
          <>
            {/* Display Selected Experience */}
            <div className="mb-6 p-4 border border-slate-600 rounded-lg bg-slate-700/50">
                <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-300">Experience:</span>
                    <span className="text-sm text-slate-100 font-medium">{formData.experience || 'N/A'}</span>
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <div className="mb-4">
                <label htmlFor="firstName" className="block text-sm font-medium text-slate-300 mb-1">First Name</label>
                <input type="text" id="firstName" value={formData.firstName || ''} onChange={e => handleChange('firstName', e.target.value)} className="w-full p-3 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:ring-lynx-primary focus:border-lynx-primary" placeholder="Enter your first name" />
              </div>
              <div className="mb-4">
                <label htmlFor="lastName" className="block text-sm font-medium text-slate-300 mb-1">Last Name</label>
                <input type="text" id="lastName" value={formData.lastName || ''} onChange={e => handleChange('lastName', e.target.value)} className="w-full p-3 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:ring-lynx-primary focus:border-lynx-primary" placeholder="Enter your last name" />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-300 mb-1">Phone Number</label>
              <input type="tel" id="phoneNumber" value={formData.phoneNumber || ''} onChange={e => handleChange('phoneNumber', e.target.value)} className="w-full p-3 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:ring-lynx-primary focus:border-lynx-primary" placeholder="e.g., (555) 123-4567" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                <input type="email" id="email" value={formData.email || ''} onChange={e => handleChange('email', e.target.value)} className="w-full p-3 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:ring-lynx-primary focus:border-lynx-primary" placeholder="you@example.com" />
              </div>
              <div className="mb-4">
                <ZipCodeAutocomplete
                  id="zipCode"
                  label="Zip Code"
                  value={formData.zipCode || ''}
                  onChange={(value) => handleChange('zipCode', value)}
                  placeholder="Enter your zip code"
                />
              </div>
            </div>

            <div className="mb-4 relative">
              <label htmlFor="password"className="block text-sm font-medium text-slate-300 mb-1">Create Password</label>
              <input type={showPassword ? 'text' : 'password'} id="password" value={formData.password || ''} onChange={e => handleChange('password', e.target.value)} className="w-full p-3 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:ring-lynx-primary focus:border-lynx-primary" placeholder="Enter a strong password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 top-7 pr-3 flex items-center text-slate-400 hover:text-slate-200">
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>

            <div className="mb-6 relative">
              <label htmlFor="confirmPassword"className="block text-sm font-medium text-slate-300 mb-1">Confirm Password</label>
              <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword || ''} onChange={e => handleChange('confirmPassword', e.target.value)} className="w-full p-3 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:ring-lynx-primary focus:border-lynx-primary" placeholder="Confirm your password" />
               <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 top-7 pr-3 flex items-center text-slate-400 hover:text-slate-200">
                {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </>
        );
      
      case 2: // Instrument Selection step
        return (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">What instruments do you teach?</h3>
              <p className="text-sm text-slate-400 mb-4">Select all instruments you're qualified to teach.</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                {availableInstruments.map((instrument) => (
                  <label key={instrument} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-700/30 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.instrumentsTaught?.includes(instrument) || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Add instrument
                          const newInstruments = [...(formData.instrumentsTaught || []), instrument];
                          const newSpecialty = newInstruments.length === 1 ? instrument : formData.specialtyInstrument;
                          console.log(`🎵 Added ${instrument}. Total instruments: ${newInstruments.length}. Specialty: ${newSpecialty}`);
                          setFormData(prev => ({ 
                            ...prev, 
                            instrumentsTaught: newInstruments,
                            // If this is the first instrument, set it as specialty
                            specialtyInstrument: newSpecialty
                          }));
                        } else {
                          // Remove instrument
                          const newInstruments = (formData.instrumentsTaught || []).filter(item => item !== instrument);
                          const newSpecialty = formData.specialtyInstrument === instrument 
                            ? (newInstruments.length === 1 ? newInstruments[0] : '') 
                            : (newInstruments.length === 1 ? newInstruments[0] : formData.specialtyInstrument);
                          console.log(`🎵 Removed ${instrument}. Total instruments: ${newInstruments.length}. Specialty: ${newSpecialty}`);
                          setFormData(prev => ({ 
                            ...prev, 
                            instrumentsTaught: newInstruments,
                            // If removing specialty instrument or only one left, update specialty
                            specialtyInstrument: newSpecialty
                          }));
                        }
                      }}
                      className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                    />
                    <span className="text-sm text-slate-200">{instrument}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Specialty Instrument Selection - only show if more than one instrument is selected */}
            {formData.instrumentsTaught && formData.instrumentsTaught.length > 1 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-3">What's your specialty instrument?</h3>
                <p className="text-sm text-slate-400 mb-4">Choose your strongest/primary instrument that you're most passionate about teaching.</p>
                
                <div className="space-y-2">
                  {formData.instrumentsTaught.map((instrument) => (
                    <label key={instrument} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-700/30 p-2 rounded">
                      <input
                        type="radio"
                        name="specialtyInstrument"
                        value={instrument}
                        checked={formData.specialtyInstrument === instrument}
                        onChange={(e) => handleChange('specialtyInstrument', e.target.value)}
                        className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 focus:ring-yellow-400 focus:ring-2"
                      />
                      <span className="text-sm text-slate-200">{instrument}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        );

      case 3: // Age Range Selection step
        return (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">What age groups do you teach?</h3>
              <p className="text-sm text-slate-400 mb-4">Select all age ranges you're comfortable teaching.</p>
              
              <div className="space-y-3">
                {ageGroupOptions.map((ageGroup) => (
                  <label key={ageGroup} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-3 rounded-lg border border-slate-600/50">
                    <input
                      type="checkbox"
                      checked={formData.agesTaught?.includes(ageGroup) || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleMultiCheckboxChange('agesTaught', ageGroup);
                        } else {
                          handleMultiCheckboxChange('agesTaught', ageGroup);
                          // If unchecking specialty age group, clear it
                          if (formData.specializedAgeGroup === ageGroup) {
                            handleChange('specializedAgeGroup', '');
                          }
                        }
                      }}
                      className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                    />
                    <span className="text-slate-200 font-medium">{ageGroup}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Specialty Age Group Selection - only show if age groups are selected */}
            {formData.agesTaught && formData.agesTaught.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-3">What's your specialty age group?</h3>
                <p className="text-sm text-slate-400 mb-4">Choose the age group you're most passionate about teaching or have the most experience with.</p>
                
                <div className="space-y-2">
                  {formData.agesTaught.map((ageGroup) => (
                    <label key={ageGroup} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-3 rounded-lg">
                      <input
                        type="radio"
                        name="specializedAgeGroup"
                        value={ageGroup}
                        checked={formData.specializedAgeGroup === ageGroup}
                        onChange={(e) => handleChange('specializedAgeGroup', e.target.value)}
                        className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 focus:ring-yellow-400 focus:ring-2"
                      />
                      <span className="text-slate-200 font-medium">{ageGroup}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        );

      case 4: // Skill Level Selection step
        return (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">What skill levels do you teach?</h3>
              <p className="text-sm text-slate-400 mb-4">For each instrument, select the skill levels you're comfortable teaching.</p>
              <p className="text-sm text-yellow-400 mb-6">
                Optional: Choose up to 2 skill levels you'd like to focus on teaching.
              </p>
              
              <div className="space-y-6">
                {formData.instrumentsTaught?.map((instrument) => {
                  const selectedSkillLevels = formData.instrumentSkillLevels?.[instrument] || [];
                  const specialtySkillLevel = formData.specialtySkillLevels?.[instrument];
                  
                  return (
                    <div key={instrument} className="p-4 border border-slate-600 rounded-lg bg-slate-700/30">
                      <h4 className="font-semibold text-slate-100 mb-3 text-base">{instrument}</h4>
                      
                      {/* Skill Level Selection */}
                      <div className="mb-4">
                        <p className="text-sm text-slate-400 mb-3">Select all skill levels you can teach:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {skillLevels.map((skillLevel) => {
                            const isSelected = selectedSkillLevels.includes(skillLevel);
                            return (
                              <label 
                                key={skillLevel} 
                                className={`flex items-center justify-center space-x-2 cursor-pointer p-3 rounded-lg border transition-all ${
                                  isSelected 
                                    ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-200' 
                                    : 'bg-slate-600/30 border-slate-600 text-slate-300 hover:bg-slate-600/50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSkillLevelChange(instrument, skillLevel)}
                                  className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                                />
                                <span className="text-sm font-medium">{skillLevel}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      {/* Specialty Skill Level Selection - only show if skill levels are selected */}
                      {selectedSkillLevels.length > 0 && (
                        <div>
                          <p className="text-sm text-slate-400 mb-3">
                            Optional: Choose a skill level you'd like to focus on for {instrument}:
                            <span className="ml-2 text-yellow-400 text-xs">
                              ({Object.keys(formData.specialtySkillLevels || {}).filter(inst => formData.specialtySkillLevels?.[inst]).length}/2 focus areas selected)
                            </span>
                          </p>
                          <div className="space-y-2">
                            {/* Clear/None option */}
                            <label className="flex items-center space-x-3 cursor-pointer hover:bg-slate-600/30 p-2 rounded-lg">
                              <input
                                type="radio"
                                name={`specialty-${instrument}`}
                                value=""
                                checked={!specialtySkillLevel}
                                onChange={() => {
                                  setFormData(prev => {
                                    const updatedSpecialties = { ...prev.specialtySkillLevels };
                                    delete updatedSpecialties[instrument];
                                    return { ...prev, specialtySkillLevels: updatedSpecialties };
                                  });
                                }}
                                className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 focus:ring-yellow-400 focus:ring-2"
                              />
                              <span className="text-sm text-slate-400 italic">No focus area for this instrument</span>
                            </label>
                            
                            {selectedSkillLevels.map((skillLevel) => (
                              <label 
                                key={skillLevel} 
                                className="flex items-center space-x-3 cursor-pointer hover:bg-slate-600/30 p-2 rounded-lg"
                              >
                                <input
                                  type="radio"
                                  name={`specialty-${instrument}`}
                                  value={skillLevel}
                                  checked={specialtySkillLevel === skillLevel}
                                  onChange={() => handleSpecialtySkillLevelChange(instrument, skillLevel)}
                                  className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 focus:ring-yellow-400 focus:ring-2"
                                />
                                <span className="text-sm text-slate-200">{skillLevel}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }) || (
                  <div className="text-center p-6 border border-slate-600 rounded-lg bg-slate-700/30">
                    <p className="text-slate-400">No instruments selected. Please go back to step 2 to select instruments.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        );

      case 5: // Teaching Specialty Selection step
        return (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">What teaching specialties do you offer?</h3>
              <p className="text-sm text-slate-400 mb-4">Select all genres and teaching focuses you're qualified to teach.</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
                {teachingSpecialties.map((specialty) => (
                  <label key={specialty} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-700/30 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.teachingSpecialties?.includes(specialty) || false}
                      onChange={() => handleMultiCheckboxChange('teachingSpecialties', specialty)}
                      className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                    />
                    <span className="text-sm text-slate-200">{specialty}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Specialized Teaching Area Selection - only show if specialties are selected */}
            {formData.teachingSpecialties && formData.teachingSpecialties.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Choose your top 3 specializations</h3>
                <p className="text-sm text-slate-400 mb-4">
                  Select up to 3 areas where you excel the most. These will be highlighted on your profile.
                  <span className="block mt-1 text-yellow-400">
                    Selected: {formData.specializedTeachingAreas?.length || 0}/3
                  </span>
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.teachingSpecialties.map((specialty) => {
                    const isSelected = formData.specializedTeachingAreas?.includes(specialty) || false;
                    const isAtLimit = (formData.specializedTeachingAreas?.length || 0) >= 3;
                    
                    return (
                      <label 
                        key={specialty} 
                        className={`flex items-center space-x-3 cursor-pointer p-3 rounded-lg border transition-all ${
                          isSelected 
                            ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-200' 
                            : isAtLimit 
                              ? 'bg-slate-600/20 border-slate-600/30 text-slate-500 cursor-not-allowed'
                              : 'bg-slate-600/30 border-slate-600 text-slate-300 hover:bg-slate-600/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSpecializedTeachingAreaChange(specialty)}
                          disabled={!isSelected && isAtLimit}
                          className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2 disabled:opacity-50"
                        />
                        <span className="text-sm font-medium">{specialty}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        );

      case 6: // Languages Spoken step
        return (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">What languages do you speak?</h3>
              <p className="text-sm text-slate-400 mb-4">Select all languages you're comfortable teaching in.</p>
              
              <div className="space-y-3">
                {availableLanguages.map((language) => (
                  <label key={language} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-3 rounded-lg border border-slate-600/50">
                    <input
                      type="checkbox"
                      checked={formData.languagesSpoken?.includes(language) || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleMultiCheckboxChange('languagesSpoken', language);
                        } else {
                          handleMultiCheckboxChange('languagesSpoken', language);
                        }
                      }}
                      className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                    />
                    <span className="text-slate-200 font-medium">{language}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        );

      case 7: // Lesson Types step
        return (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">What types of lessons do you offer?</h3>
              <p className="text-sm text-slate-400 mb-4">Select all lesson formats you're comfortable with.</p>
              
              <div className="space-y-3">
                {lessonTypeOptions.map((lessonType) => (
                  <label key={lessonType} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-3 rounded-lg border border-slate-600/50">
                    <input
                      type="checkbox"
                      checked={formData.locationsOffered?.includes(lessonType) || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleMultiCheckboxChange('locationsOffered', lessonType);
                        } else {
                          handleMultiCheckboxChange('locationsOffered', lessonType);
                          // If unchecking student home visits, clear travel distance
                          if (lessonType === 'In-home (student\'s home)') {
                            handleChange('maxTravelDistance', '');
                          }
                        }
                      }}
                      className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                    />
                    <span className="text-slate-200 font-medium">{lessonType}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Travel Distance Selection - only show if student home visits selected */}
            {formData.locationsOffered?.includes('In-home (student\'s home)') && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-3">What's your maximum travel distance?</h3>
                <p className="text-sm text-slate-400 mb-4">Select the maximum distance you're willing to travel to a student's home.</p>
                
                <div className="space-y-3">
                  {travelDistanceOptions.map((distance) => (
                    <label key={distance} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-3 rounded-lg">
                      <input
                        type="radio"
                        name="maxTravelDistance"
                        value={distance}
                        checked={formData.maxTravelDistance === distance}
                        onChange={(e) => handleChange('maxTravelDistance', e.target.value)}
                        className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 focus:ring-yellow-400 focus:ring-2"
                      />
                      <span className="text-slate-200 font-medium">{distance}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        );

      case 8: // Availability step
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">When do you want to start working?</h3>
              <p className="text-sm text-slate-400 mb-4">
                Select the days you're generally available to teach. You can set specific times later.
              </p>
            </div>

            {/* Option to set up later */}
            <div className="mb-6">
              <label className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10">
                <input
                  type="checkbox"
                  checked={formData.setupAvailabilityLater || false}
                  onChange={(e) => {
                    console.log('🗓️ Setup availability later toggled:', e.target.checked);
                    handleChange('setupAvailabilityLater', e.target.checked);
                    // Clear availability if choosing to set up later
                    if (e.target.checked) {
                      handleChange('availability', {});
                    }
                  }}
                  className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                />
                <div>
                  <span className="text-yellow-200 font-medium">📅 Set up availability later</span>
                  <p className="text-sm text-slate-400">I'll configure my teaching schedule in the dashboard</p>
                </div>
              </label>
            </div>

            {!formData.setupAvailabilityLater && (
              <div>
                <h4 className="text-md font-medium text-slate-200 mb-4">
                  Or select your general availability now:
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {daysOfWeek.map((day) => {
                    const isSelected = formData.availability && formData.availability[day];
                    return (
                      <label key={day} className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-3 rounded-lg border border-slate-600/50">
                        <input
                          type="checkbox"
                          checked={isSelected || false}
                          onChange={(e) => {
                            console.log(`🗓️ ${day} availability toggled:`, e.target.checked);
                            const currentAvailability = formData.availability || {};
                            
                            if (e.target.checked) {
                              // Add the day (mark as available)
                              const updatedAvailability = {
                                ...currentAvailability,
                                [day]: true
                              };
                              handleChange('availability', updatedAvailability);
                            } else {
                              // Remove the day
                              const { [day]: removed, ...updatedAvailability } = currentAvailability;
                              handleChange('availability', updatedAvailability);
                            }
                          }}
                          className="w-4 h-4 text-yellow-500 bg-slate-700 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                        />
                        <span className="text-slate-200 font-medium">{day}</span>
                      </label>
                    );
                  })}
                </div>
                
                <div className="mt-4 text-sm text-slate-400">
                  <p>💡 <strong>Tip:</strong> You can set specific time slots and detailed availability in your teacher dashboard after registration.</p>
                </div>
              </div>
            )}
          </div>
        );

      // Cases for other steps will go here
      // ...
      default:
        return <p className="text-slate-300">Step {currentStep} - Content to be added.</p>;
    }
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex items-start w-full max-w-2xl gap-x-2 sm:gap-x-3 md:gap-x-4">
          
          <div className="flex-shrink-0">
            <button 
              onClick={handleBack} 
              className="text-slate-300 hover:text-slate-100 p-3 rounded-full hover:bg-slate-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 mt-5 md:mt-7"
              aria-label="Go back"
            >
              <ChevronLeftIcon className="h-6 w-6 md:h-7 md:w-7" />
            </button>
          </div>

          <div className="flex-grow flex justify-center min-w-0">
            <div className="bg-slate-800 p-8 md:p-10 rounded-xl shadow-2xl min-h-[400px] w-full max-w-lg">
            {currentStep === 1 ? (
                 <h1 className="text-3xl font-bold text-white text-center mb-2">Set Up Your Teacher Profile</h1>
            ) : (
                 <h1 className="text-3xl font-bold text-white text-center mb-6">Teacher Profile Details</h1>
            )}
            {currentStep === 1 && (
                 <p className="text-sm text-slate-400 text-center mb-8">Create your private online lesson studio.</p>
            )}
            {renderStepContent()}
            
            <div className="mt-10">
              <button 
                onClick={handleNext} 
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold py-3 px-6 rounded-lg shadow-md transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-75"
              >
                                    {currentStep === TOTAL_STEPS ? 'Submit Profile' : 'Next'}
              </button>
            </div>
            {currentStep === 1 && (
              <div className="mt-6 text-center">
                <p className="text-xs text-slate-500">
                  By continuing, I agree to LessonLynx's <Link to="/terms-of-service" className="underline hover:text-yellow-500">Terms of Use</Link> and <Link to="/privacy-policy" className="underline hover:text-yellow-500">Privacy Policy</Link>.
                </p>
                <p className="text-sm text-slate-400 mt-4">
                  Already have an account? <Link to="/login" className="font-semibold text-yellow-500 hover:text-yellow-400">Sign In</Link>
                </p>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default TeacherQuestionnairePage; 